import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import {
  ActionConfig,
  baseCssConfigStyle,
  Gestures,
  ListingAction,
  isGesturesEnabled,
} from '@finxone-platform/shared/sys-config-types';
import Handlebars from 'handlebars';
import { HandlebarModelService } from '@app/finxone-web-frontend/app/lib/services/handlebar-model-service/handlebar-model.service';
import { GestureModule } from '@app/finxone-web-frontend/app/lib/gestures/gesture.module';
import { PanRevealComponent } from '@app/finxone-web-frontend/app/lib/gestures/components/pan-reveal/pan-reveal.component';

@Component({
  selector: 'finxone-web-frontend-list-item',
  imports: [CommonModule, SafeHtmlPipe, GestureModule, PanRevealComponent, SafeHtmlPipe],
  templateUrl: './list-item.component.html',
  styleUrl: './list-item.component.scss',
})
export class ListItemComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Input() textContentStyle: baseCssConfigStyle;
  @Input() customHandlebarModel: any;
  @Input() content: string;
  @Input() customClass: string;
  @Input() gestures: Gestures | undefined;

  // replace direct Input with setter so we can compute a stable cache
  private _listActions: ListingAction;
  @Input()
  set listActions(val: ListingAction) {
    this._listActions = val;
    this.buildActionButtons();
  }
  get listActions() {
    return this._listActions;
  }

  // separated caches for left and right positioned actions
  public actionButtonsLeft: Array<any> = [];
  public actionButtonsRight: Array<any> = [];

  @Output() actionTriggered = new EventEmitter<{ action: string; config: any }>();
  @Output() itemClicked = new EventEmitter<void>();
  public baseHandleBarModel: any;
  public templateFunction: HandlebarsTemplateDelegate<any>;

  // simple cache to avoid recompiling identical templates
  private static templateCache = new Map<string, HandlebarsTemplateDelegate<any>>();

  // cache for small ad-hoc templates (like action titles) to avoid churn
  private _handlebarTemplateCache = new Map<string, HandlebarsTemplateDelegate<any>>();

  constructor(private handlebarModelService: HandlebarModelService) {}

  ngOnInit() {
    // use the cached model from the service, and unsubscribe on destroy
    this.handlebarModelService.modelCache$.pipe(takeUntil(this.destroy$)).subscribe((model) => {
      this.baseHandleBarModel = model;
    });

    const key = this.content || '__empty__';
    if (!ListItemComponent.templateCache.has(key)) {
      ListItemComponent.templateCache.set(key, Handlebars.compile(this.content || ''));
    }
    this.templateFunction = ListItemComponent.templateCache.get(key)!;
  }

  public createTemplateData() {
    return this.templateFunction({
      ...this.baseHandleBarModel,
      ...this.customHandlebarModel,
    });
  }

  // build a stable array of action button objects (called only when listActions changes)
  private buildActionButtons() {
    const listActions = this._listActions;
    if (!listActions || !Array.isArray(listActions.actions)) {
      this.actionButtonsLeft = [];
      this.actionButtonsRight = [];
      return;
    }

    const configs: ActionConfig[] = Array.isArray(listActions.actionsConfig) ? listActions.actionsConfig : [];
    const rowGap = listActions.rowGap || 0;

    // create flat items preserving original action order and ensuring position defaults to 'right'
    const items = listActions.actions.map((actionName: string, i: number) => {
      const cfg: ActionConfig | undefined = configs.find((c) => c && c.action === actionName);
      return {
        ...(cfg || {}),
        action: actionName,
        position: cfg?.position ?? 'right', // ensure position exists and defaults to 'right'
        class: cfg?.class || 'btn',
        actionTitle: cfg?.actionTitle ?? actionName,
        navigationOptionConfig: cfg?.navigationOptionConfig || null,
        linkToGlobalStyling: !!cfg?.linkToGlobalStyling,
        isActionSelected: !!cfg?.isActionSelected,
        rawConfig: cfg || null,
        // ensure _style is an indexable object so we can assign keys like 'margin-right.px'
        _style: {} as Record<string, any>,
      };
    });

    // split into sides while preserving original relative ordering
    const leftItems = items.filter((item) => item.position === 'left');
    const rightItems = items.filter((item) => item.position !== 'left');

    // apply spacing styles:
    // - left actions: margin-right between items (so they appear spaced from left edge inward)
    leftItems.forEach((item, idx) => {
      item._style = item._style || ({} as Record<string, any>);
      item._style['margin-right.px'] = idx === leftItems.length - 1 ? 0 : rowGap;
    });

    // - right actions: margin-left between items (so they appear spaced from the right edge outward)
    rightItems.forEach((item, idx) => {
      item._style = item._style || ({} as Record<string, any>);
      item._style['margin-left.px'] = idx === 0 ? 0 : rowGap;
    });

    // update caches used by template
    this.actionButtonsLeft = leftItems;
    this.actionButtonsRight = rightItems;
  }

  public onAction(actionCfg: any, evt?: Event) {
    if (evt) evt.stopPropagation();
    this.actionTriggered.emit({ action: actionCfg.action, config: actionCfg });
  }

  public onItemClick() {
    // Prevent emitting a click when gestures are enabled; gestures handle the interaction.
    if (this.gestures && isGesturesEnabled(this.gestures)) return;
    this.itemClicked.emit();
  }

  // trackBy to keep DOM stable
  public trackByAction(index: number, item: any) {
    return item?.action ?? index;
  }

  /**
   * Render a Handlebars template string to HTML using the merged model.
   * Uses an internal cache to avoid recompiling identical template strings.
   */
  public renderHandlebarsTemplate(templateString: string, context?: any): string {
    if (!templateString && templateString !== '') return '';
    try {
      let fn = this._handlebarTemplateCache.get(templateString);
      if (!fn) {
        fn = Handlebars.compile(templateString);
        this._handlebarTemplateCache.set(templateString, fn);
      }
      const merged = {
        ...(this.baseHandleBarModel || {}),
        ...(this.customHandlebarModel || {}),
        ...(context || {}),
      };
      return fn(merged);
    } catch (e) {
      // on error, fallback to returning the raw template string to avoid crashes
      return templateString;
    }
  }
}
